/**
 * Teams Management View
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  Alert,
  CircularProgress,
  Tab,
  Tabs,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Menu,
} from '@mui/material';
import {
  Add as AddIcon,
  People as PeopleIcon,
  PersonAdd as PersonAddIcon,
  Email as EmailIcon,
  Shield as ShieldIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  LockReset as LockResetIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useBOM } from '../contexts/BOMContext';
import * as authApi from '../services/authApi';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const TeamsView: React.FC = () => {
  const { user, token } = useAuth();
  const { showToast } = useBOM();
  const [currentTab, setCurrentTab] = useState(0);
  
  // Teams state
  const [teams, setTeams] = useState<authApi.Team[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<authApi.TeamDetails | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  
  // Users state
  const [users, setUsers] = useState<authApi.UserResponse[]>([]);
  const [roles, setRoles] = useState<authApi.Role[]>([]);
  const [organizations, setOrganizations] = useState<authApi.Organization[]>([]);
  
  // Dialogs
  const [createTeamDialog, setCreateTeamDialog] = useState(false);
  const [createUserDialog, setCreateUserDialog] = useState(false);
  const [inviteDialog, setInviteDialog] = useState(false);
  
  // Form data
  const [newTeamName, setNewTeamName] = useState('');
  const [newTeamDescription, setNewTeamDescription] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRoleId, setInviteRoleId] = useState('');
  const [newUserData, setNewUserData] = useState({
    email: '',
    username: '',
    password: '',
    full_name: '',
    is_admin: false,
    role_ids: [] as string[],
    organization_ids: [] as string[],
  });
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; userId: string; username: string }>({
    open: false,
    userId: '',
    username: '',
  });
  const [resetPasswordDialog, setResetPasswordDialog] = useState<{ open: boolean; userId: string; username: string }>({
    open: false,
    userId: '',
    username: '',
  });
  const [userMenuAnchor, setUserMenuAnchor] = useState<{ [key: string]: HTMLElement | null }>({});
  const [deleteTeamDialog, setDeleteTeamDialog] = useState<{ open: boolean; teamId: string; teamName: string }>({
    open: false,
    teamId: '',
    teamName: '',
  });

  useEffect(() => {
    if (token) {
      loadTeams();
      loadUsers();
      loadRoles();
      loadOrganizations();
    }
  }, [token]);

  const loadTeams = async () => {
    if (!token) return;
    try {
      setIsLoading(true);
      const data = await authApi.listTeams(token);
      setTeams(data.teams);
    } catch (error: any) {
      showToast('error', `Failed to load teams: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const loadUsers = async () => {
    if (!token) return;
    try {
      const data = await authApi.listUsers(token);
      setUsers(data.users);
    } catch (error: any) {
      showToast('error', `Failed to load users: ${error.message}`);
    }
  };

  const loadRoles = async () => {
    try {
      const data = await authApi.listRoles();
      setRoles(data.roles);
    } catch (error: any) {
      showToast('error', `Failed to load roles: ${error.message}`);
    }
  };

  const loadOrganizations = async () => {
    if (!token) return;
    try {
      const data = await authApi.getOrganizations(token);
      setOrganizations(data.organizations);
    } catch (error: any) {
      showToast('error', `Failed to load organizations: ${error.message}`);
    }
  };

  const loadTeamDetails = async (teamId: string) => {
    if (!token) return;
    try {
      const data = await authApi.getTeam(token, teamId);
      setSelectedTeam(data.team);
    } catch (error: any) {
      showToast('error', `Failed to load team details: ${error.message}`);
    }
  };

  const handleCreateTeam = async () => {
    if (!token || !newTeamName) return;
    
    try {
      setIsLoading(true);
      await authApi.createTeam(token, {
        name: newTeamName,
        description: newTeamDescription,
      });
      showToast('success', 'Team created successfully!');
      setCreateTeamDialog(false);
      setNewTeamName('');
      setNewTeamDescription('');
      loadTeams();
    } catch (error: any) {
      showToast('error', `Failed to create team: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteTeam = async () => {
    if (!token || !deleteTeamDialog.teamId) return;
    
    try {
      setIsLoading(true);
      await authApi.deleteTeam(token, deleteTeamDialog.teamId);
      showToast('success', 'Team deleted successfully!');
      setDeleteTeamDialog({ open: false, teamId: '', teamName: '' });
      loadTeams();
    } catch (error: any) {
      showToast('error', `Failed to delete team: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateUser = async () => {
    if (!token || !newUserData.email || !newUserData.username) {
      showToast('error', 'Please fill in required fields: email and username');
      return;
    }
    
    // Validate at least one organization is selected
    if (newUserData.organization_ids.length === 0) {
      showToast('error', 'Please select at least one organization');
      return;
    }
    
    try {
      setIsLoading(true);
      await authApi.createUser(token, {
        ...newUserData,
        role_ids: newUserData.role_ids.length > 0 ? newUserData.role_ids : undefined,
        // Don't send password - let backend auto-generate
        password: undefined,
      });
      showToast('success', 'User created successfully! Onboarding email sent.');
      setCreateUserDialog(false);
      setNewUserData({
        email: '',
        username: '',
        password: '',
        full_name: '',
        is_admin: false,
        role_ids: [],
        organization_ids: [],
      });
      loadUsers();
    } catch (error: any) {
      showToast('error', `Failed to create user: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!token || !deleteDialog.userId) return;
    
    try {
      setIsLoading(true);
      await authApi.deleteUser(token, deleteDialog.userId);
      showToast('success', 'User deleted successfully!');
      setDeleteDialog({ open: false, userId: '', username: '' });
      loadUsers();
    } catch (error: any) {
      showToast('error', `Failed to delete user: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!token || !resetPasswordDialog.userId) return;

    setIsLoading(true);
    try {
      await authApi.resetUserPassword(token, resetPasswordDialog.userId, true, true);
      showToast('success', `Password reset email sent to ${resetPasswordDialog.username}`);
      setResetPasswordDialog({ open: false, userId: '', username: '' });
    } catch (error: any) {
      showToast('error', `Failed to reset password: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>, userId: string) => {
    setUserMenuAnchor({ ...userMenuAnchor, [userId]: event.currentTarget });
  };

  const handleUserMenuClose = (userId: string) => {
    setUserMenuAnchor({ ...userMenuAnchor, [userId]: null });
  };

  const handleSendInvite = async () => {
    if (!token || !selectedTeam || !inviteEmail) return;
    
    try {
      setIsLoading(true);
      await authApi.inviteToTeam(token, selectedTeam.id, {
        email: inviteEmail,
        role_id: inviteRoleId || undefined,
      });
      showToast('success', 'Invitation sent successfully!');
      setInviteDialog(false);
      setInviteEmail('');
      setInviteRoleId('');
    } catch (error: any) {
      showToast('error', `Failed to send invitation: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  if (!user) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h6" color="text.secondary">
          Please log in to access teams.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={currentTab} onChange={(_, val) => setCurrentTab(val)}>
          <Tab label="My Teams" icon={<PeopleIcon />} iconPosition="start" />
          <Tab label="Users" icon={<ShieldIcon />} iconPosition="start" />
          <Tab label="Roles" icon={<ShieldIcon />} iconPosition="start" />
        </Tabs>
      </Box>

      {/* Teams Tab */}
      <TabPanel value={currentTab} index={0}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" fontWeight={600}>
            Team Management
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateTeamDialog(true)}
          >
            Create Team
          </Button>
        </Box>

        {isLoading && teams.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : teams.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <PeopleIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              No teams yet
            </Typography>
            <Typography color="text.secondary" paragraph>
              Create a team to collaborate with others
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateTeamDialog(true)}
            >
              Create Your First Team
            </Button>
          </Paper>
        ) : (
          <Grid container spacing={3}>
            {teams.map((team) => (
              <Grid item xs={12} sm={6} md={4} key={team.id}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {team.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      {team.description || 'No description'}
                    </Typography>
                    <Chip
                      icon={<PeopleIcon />}
                      label={`${team.member_count} members`}
                      size="small"
                    />
                  </CardContent>
                  <CardActions sx={{ justifyContent: 'space-between' }}>
                    <Button
                      size="small"
                      onClick={() => loadTeamDetails(team.id)}
                    >
                      View Details
                    </Button>
                    {user?.is_admin && (
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => setDeleteTeamDialog({ open: true, teamId: team.id, teamName: team.name })}
                        title="Delete Team"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </TabPanel>

      {/* Users Tab */}
      <TabPanel value={currentTab} index={1}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" fontWeight={600}>
            Users
          </Typography>
          {user.is_admin && (
            <Button
              variant="contained"
              startIcon={<PersonAddIcon />}
              onClick={() => setCreateUserDialog(true)}
            >
              Create User
            </Button>
          )}
        </Box>

        <Paper>
          <List>
            {users.map((u, index) => (
              <React.Fragment key={u.id}>
                {index > 0 && <Divider />}
                <ListItem>
                  <ListItemAvatar>
                    <Avatar>{u.username[0].toUpperCase()}</Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {u.full_name || u.username}
                        {u.is_superadmin && (
                          <Chip label="Super Admin" size="small" color="error" />
                        )}
                        {u.is_admin && !u.is_superadmin && (
                          <Chip label="Admin" size="small" color="warning" />
                        )}
                      </Box>
                    }
                    secondary={
                      <Box component="span">
                        <Box component="span" sx={{ display: 'block' }}>{u.email}</Box>
                        {u.roles.length > 0 && (
                          <Box sx={{ mt: 0.5, display: 'block' }}>
                            {u.roles.map((role) => (
                              <Chip
                                key={role.id}
                                label={role.display_name}
                                size="small"
                                variant="outlined"
                                sx={{ mr: 0.5 }}
                              />
                            ))}
                          </Box>
                        )}
                      </Box>
                    }
                  />
                  {user?.is_admin && !u.is_superadmin && u.id !== user?.id && (
                    <IconButton
                      edge="end"
                      color="error"
                      onClick={() => setDeleteDialog({ open: true, userId: u.id, username: u.username })}
                      title="Delete User"
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        </Paper>
      </TabPanel>

      {/* Roles Tab */}
      <TabPanel value={currentTab} index={2}>
        <Typography variant="h5" fontWeight={600} gutterBottom>
          System Roles
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Predefined roles with specific permissions
        </Typography>

        <Grid container spacing={3}>
          {roles.map((role) => (
            <Grid item xs={12} md={6} key={role.id}>
              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <ShieldIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6">{role.display_name}</Typography>
                    {role.is_system_role && (
                      <Chip label="System" size="small" sx={{ ml: 1 }} />
                    )}
                  </Box>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {role.description}
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="caption" fontWeight={600} display="block" gutterBottom>
                    Permissions:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {Object.entries(role.permissions).map(([key, value]) => (
                      value && (
                        <Chip
                          key={key}
                          label={key.replace('can_', '').replace(/_/g, ' ')}
                          size="small"
                          variant="outlined"
                          color="primary"
                        />
                      )
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </TabPanel>

      {/* Create Team Dialog */}
      <Dialog open={createTeamDialog} onClose={() => setCreateTeamDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Create New Team
          <IconButton
            onClick={() => setCreateTeamDialog(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Team Name"
            value={newTeamName}
            onChange={(e) => setNewTeamName(e.target.value)}
            sx={{ mb: 2, mt: 1 }}
          />
          <TextField
            fullWidth
            label="Description"
            multiline
            rows={3}
            value={newTeamDescription}
            onChange={(e) => setNewTeamDescription(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateTeamDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateTeam} disabled={!newTeamName || isLoading}>
            Create Team
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog open={createUserDialog} onClose={() => setCreateUserDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Create New User
          <IconButton
            onClick={() => setCreateUserDialog(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={newUserData.email}
            onChange={(e) => setNewUserData({ ...newUserData, email: e.target.value })}
            sx={{ mb: 2, mt: 1 }}
          />
          <TextField
            fullWidth
            label="Username"
            value={newUserData.username}
            onChange={(e) => setNewUserData({ ...newUserData, username: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Full Name (Optional)"
            value={newUserData.full_name}
            onChange={(e) => setNewUserData({ ...newUserData, full_name: e.target.value })}
            sx={{ mb: 2 }}
          />
          
          {/* Organization Selection */}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Organizations *</InputLabel>
            <Select
              multiple
              value={newUserData.organization_ids}
              onChange={(e) => setNewUserData({ 
                ...newUserData, 
                organization_ids: typeof e.target.value === 'string' ? [e.target.value] : e.target.value 
              })}
              label="Organizations *"
              renderValue={(selected) => {
                const selectedOrgs = organizations.filter(org => selected.includes(org.id));
                return selectedOrgs.map(org => org.display_name).join(', ');
              }}
            >
              {organizations.length > 0 ? (
                organizations.map((org) => (
                  <MenuItem key={org.id} value={org.id}>
                    <Chip label={org.display_name} size="small" sx={{ mr: 1 }} />
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled>Loading organizations...</MenuItem>
              )}
            </Select>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
              Select one or more organizations for this user
            </Typography>
          </FormControl>
          
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              A temporary password will be auto-generated and sent to the user's email. 
              The user must change it on first login.
            </Typography>
          </Alert>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Role</InputLabel>
            <Select
              value={newUserData.role_ids.length > 0 ? newUserData.role_ids[0] : ''}
              onChange={(e) => setNewUserData({ ...newUserData, role_ids: e.target.value ? [e.target.value] : [] })}
              label="Role"
            >
              <MenuItem value="">Select a role (default: Editor)</MenuItem>
              {roles.map((role) => (
                <MenuItem key={role.id} value={role.id}>
                  {role.display_name} - {role.description}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {user?.is_superadmin && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                As a super admin, you can create admin users. Select "Admin" role above to grant admin privileges.
              </Typography>
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateUserDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateUser}
            disabled={!newUserData.email || !newUserData.username || newUserData.organization_ids.length === 0 || isLoading}
          >
            Create User
          </Button>
        </DialogActions>
      </Dialog>

      {/* Team Details Dialog */}
      {selectedTeam && (
        <Dialog open={!!selectedTeam} onClose={() => setSelectedTeam(null)} maxWidth="md" fullWidth>
          <DialogTitle>
            {selectedTeam.name}
            <IconButton
              onClick={() => setSelectedTeam(null)}
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" paragraph>
              {selectedTeam.description || 'No description'}
            </Typography>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Members ({selectedTeam.members.length})</Typography>
              <Button
                size="small"
                startIcon={<EmailIcon />}
                onClick={() => setInviteDialog(true)}
              >
                Invite Member
              </Button>
            </Box>

            <List>
              {selectedTeam.members.map((member, index) => (
                <React.Fragment key={member.id}>
                  {index > 0 && <Divider />}
                  <ListItem>
                    <ListItemAvatar>
                      <Avatar>{member.username[0].toUpperCase()}</Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={member.full_name || member.username}
                      secondary={
                        <>
                          {member.email}
                          {member.roles.length > 0 && (
                            <Box sx={{ mt: 0.5 }}>
                              {member.roles.map((role) => (
                                <Chip
                                  key={role.id}
                                  label={role.display_name}
                                  size="small"
                                  variant="outlined"
                                  sx={{ mr: 0.5 }}
                                />
                              ))}
                            </Box>
                          )}
                        </>
                      }
                    />
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSelectedTeam(null)}>Close</Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Invite Dialog */}
      <Dialog open={inviteDialog} onClose={() => setInviteDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Invite Member
          <IconButton
            onClick={() => setInviteDialog(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            sx={{ mb: 2, mt: 1 }}
          />
          <Alert severity="info">
            An invitation email will be sent to the specified address with a link to join the team.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={<EmailIcon />}
            onClick={handleSendInvite}
            disabled={!inviteEmail || isLoading}
          >
            Send Invitation
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete User Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, userId: '', username: '' })}>
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete user <strong>{deleteDialog.username}</strong>?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, userId: '', username: '' })}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteUser}
            disabled={isLoading}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Team Confirmation Dialog */}
      <Dialog open={deleteTeamDialog.open} onClose={() => setDeleteTeamDialog({ open: false, teamId: '', teamName: '' })}>
        <DialogTitle>Delete Team</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete team <strong>{deleteTeamDialog.teamName}</strong>?
            This action cannot be undone. All team members will be removed from this team.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTeamDialog({ open: false, teamId: '', teamName: '' })}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteTeam}
            disabled={isLoading}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog 
        open={resetPasswordDialog.open} 
        onClose={() => setResetPasswordDialog({ open: false, userId: '', username: '' })}
      >
        <DialogTitle>Reset Password</DialogTitle>
        <DialogContent>
          <Typography>
            Reset password for user <strong>{resetPasswordDialog.username}</strong>?
          </Typography>
          <Alert severity="info" sx={{ mt: 2 }}>
            A new temporary password will be generated and sent to the user's email. 
            They will be required to change it after logging in.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetPasswordDialog({ open: false, userId: '', username: '' })}>
            Cancel
          </Button>
          <Button 
            color="primary" 
            variant="contained" 
            onClick={handleResetPassword} 
            disabled={isLoading}
            startIcon={<LockResetIcon />}
          >
            Reset Password
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TeamsView;

